import React from 'react';

import { LinkButton } from '@wildidea/components/Button/LinkButton';

import { storeHref } from '../app/acquisition.js';

import * as css from './GetTheAppCTA.module.css';

type Platform = 'ios' | 'android' | 'unknown';

const detectPlatform = (): Platform => {
  if (typeof navigator === 'undefined') return 'unknown';
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/i.test(ua)) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'unknown';
};

const usePlatform = (): Platform => {
  const [platform, setPlatform] = React.useState<Platform>('unknown');
  React.useEffect(function detectPlatformOnMount() {
    setPlatform(detectPlatform());
  }, []);
  return platform;
};

const AppStoreIcon: React.FC<{ className?: string }> = ({ className }) => {
  // Unique per instance: this component renders more than once per page, and
  // duplicate SVG gradient ids resolve to the FIRST match document-wide — if
  // that instance is display:none (responsive variants), the fill vanishes.
  const gradientId = React.useId();
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 800 800"
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      <linearGradient
        id={gradientId}
        gradientUnits="userSpaceOnUse"
        x1="400.05"
        y1="798.7717"
        x2="400.05"
        y2="-1.2283"
        gradientTransform="matrix(1 0 0 -1 0 798.7717)"
      >
        <stop offset="0" stopColor="#18BFFB" />
        <stop offset="1" stopColor="#2072F3" />
      </linearGradient>
      <path
        fill={`url(#${gradientId})`}
        d="M638.4,0H161.6C72.3,0,0,72.3,0,161.6v476.9C0,727.7,72.3,800,161.6,800h476.9c89.2,0,161.6-72.3,161.6-161.6V161.6C800,72.3,727.7,0,638.4,0z"
      />
      <path
        fill="#FFFFFF"
        d="M396.6,183.8l16.2-28c10-17.5,32.3-23.4,49.8-13.4s23.4,32.3,13.4,49.8L319.9,462.4h112.9c36.6,0,57.1,43,41.2,72.8H143c-20.2,0-36.4-16.2-36.4-36.4c0-20.2,16.2-36.4,36.4-36.4h92.8l118.8-205.9l-37.1-64.4c-10-17.5-4.1-39.6,13.4-49.8c17.5-10,39.6-4.1,49.8,13.4L396.6,183.8L396.6,183.8z M256.2,572.7l-35,60.7c-10,17.5-32.3,23.4-49.8,13.4S148,614.5,158,597l26-45C213.4,542.9,237.3,549.9,256.2,572.7L256.2,572.7z M557.6,462.6h94.7c20.2,0,36.4,16.2,36.4,36.4c0,20.2-16.2,36.4-36.4,36.4h-52.6l35.5,61.6c10,17.5,4.1,39.6-13.4,49.8c-17.5,10-39.6,4.1-49.8-13.4c-59.8-103.7-104.7-181.3-134.5-233c-30.5-52.6-8.7-105.4,12.8-123.3C474.2,318.1,509.9,380,557.6,462.6L557.6,462.6z"
      />
    </svg>
  );
};

const GooglePlayIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 28.99 31.99"
    className={className}
    aria-hidden="true"
    focusable="false"
  >
    <path fill="#EA4335" d="M13.54 15.28.12 29.34a3.66 3.66 0 0 0 5.33 2.16l15.1-8.6Z" />
    <path
      fill="#FBBC04"
      d="m27.11 12.89-6.53-3.74-7.35 6.45 7.38 7.28 6.48-3.7a3.54 3.54 0 0 0 1.5-4.79 3.62 3.62 0 0 0-1.5-1.5z"
    />
    <path
      fill="#4285F4"
      d="M.12 2.66a3.57 3.57 0 0 0-.12.92v24.84a3.57 3.57 0 0 0 .12.92L14 15.64Z"
    />
    <path
      fill="#34A853"
      d="m13.64 16 6.94-6.85L5.5.51A3.73 3.73 0 0 0 3.63 0 3.64 3.64 0 0 0 .12 2.65Z"
    />
  </svg>
);

export interface GetTheAppCTAProps {
  className?: string;
  /** Render only the store badges, without the "Get the app:" label. */
  hideLabel?: boolean;
  /**
   * Always show BOTH store badges instead of narrowing to the detected
   * platform — for placements where the pair is the point (balanced layout,
   * "we're cross-platform" signal), like the hero logo flankers.
   */
  allPlatforms?: boolean;
}

export const GetTheAppCTA: React.FC<GetTheAppCTAProps> = ({
  className,
  hideLabel,
  allPlatforms
}) => {
  const platform = usePlatform();
  const showApple = allPlatforms || platform === 'ios' || platform === 'unknown';
  const showGoogle = allPlatforms || platform === 'android' || platform === 'unknown';

  const label =
    !allPlatforms && platform === 'ios'
      ? 'Get it on the App Store:'
      : !allPlatforms && platform === 'android'
        ? 'Get it on Google Play:'
        : 'Get the app:';

  return (
    <div className={[css.cta, className].filter(Boolean).join(' ')} suppressHydrationWarning>
      {!hideLabel ? (
        <span className={css.label} suppressHydrationWarning>
          {label}
        </span>
      ) : null}
      <span className={css.buttons}>
        {showApple ? (
          <LinkButton
            appearance="minimal"
            href={storeHref('ios')}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Download Rescue Ally on the App Store"
            className={css.button}
            icon={<AppStoreIcon className={css.icon} />}
          />
        ) : null}
        {showGoogle ? (
          <LinkButton
            appearance="minimal"
            href={storeHref('android')}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Get Rescue Ally on Google Play"
            className={css.button}
            icon={<GooglePlayIcon className={css.icon} />}
          />
        ) : null}
      </span>
    </div>
  );
};
